import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { Box, IconButton, Typography } from "@mui/material";
import React, { useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";
import ShakaPlayerVideo from "@/shared/components/shakaplayer/ShakaPlayerVideo";
import {
  attachCardVideoMiniPlayerMedia,
  detachCardVideoMiniPlayerMedia,
  dismissCardVideoMiniPlayer,
} from "../singleCardMedia";
import { useCardVideoMiniPlayerStore } from "../cardVideoMiniPlayerStore";

/** YouTube-style floating mini player — persists while browsing book pages. */
export function CardVideoMiniPlayer() {
  const visible = useCardVideoMiniPlayerStore((s) => s.visible);
  const kind = useCardVideoMiniPlayerStore((s) => s.kind);
  const title = useCardVideoMiniPlayerStore((s) => s.title);
  const subtitle = useCardVideoMiniPlayerStore((s) => s.subtitle);
  const mediaUrl = useCardVideoMiniPlayerStore((s) => s.mediaUrl);
  const startTime = useCardVideoMiniPlayerStore((s) => s.startTime);
  const mediaHostRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    if (!visible || !kind || !mediaHostRef.current) {
      detachCardVideoMiniPlayerMedia();
      return;
    }
    attachCardVideoMiniPlayerMedia(mediaHostRef.current, kind);
    return () => {
      detachCardVideoMiniPlayerMedia();
    };
  }, [visible, kind, mediaUrl]);

  if (!visible || typeof document === "undefined") return null;

  return createPortal(
    <Box
      role="region"
      aria-label="Mini video player"
      sx={{
        position: "fixed",
        right: { xs: 12, sm: 20 },
        left: "auto",
        bottom: { xs: "max(12px, env(safe-area-inset-bottom, 12px))", sm: 20 },
        zIndex: 1700,
        width: { xs: "min(72vw, 280px)", sm: 300 },
        borderRadius: "14px",
        overflow: "hidden",
        bgcolor: "#0B0B0F",
        border: "1px solid rgba(255,255,255,0.14)",
        boxShadow: "0 18px 48px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.06)",
      }}
    >
      <Box
        ref={mediaHostRef}
        sx={{
          position: "relative",
          width: "100%",
          aspectRatio: "16 / 9",
          bgcolor: "#000",
          "& iframe, & video, & .shaka-player-container": {
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            border: "none",
            display: "block",
            objectFit: "contain",
          },
        }}
      >
        {kind === "shaka" && mediaUrl ? (
          <ShakaPlayerVideo src={mediaUrl} autoPlay startTime={startTime} />
        ) : null}
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 0.75,
          px: 1.1,
          py: 0.85,
          bgcolor: "rgba(8,8,12,0.96)",
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            noWrap
            sx={{
              fontFamily: '"DM Sans", sans-serif',
              fontSize: "0.78rem",
              fontWeight: 700,
              color: "#fff",
              lineHeight: 1.25,
            }}
          >
            {title}
          </Typography>
          {subtitle ? (
            <Typography
              noWrap
              sx={{
                fontFamily: '"DM Sans", sans-serif',
                fontSize: "0.68rem",
                color: "rgba(255,255,255,0.58)",
                mt: 0.15,
              }}
            >
              {subtitle}
            </Typography>
          ) : null}
        </Box>
        <IconButton
          aria-label="Close mini player"
          size="small"
          onClick={() => dismissCardVideoMiniPlayer()}
          sx={{
            color: "rgba(255,255,255,0.82)",
            bgcolor: "rgba(255,255,255,0.08)",
            width: 28,
            height: 28,
            "&:hover": { bgcolor: "rgba(255,255,255,0.14)" },
          }}
        >
          <CloseRoundedIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
    </Box>,
    document.body
  );
}

export default CardVideoMiniPlayer;
